import { z } from "zod";

/**
 * Validation schemas for application data
 * These ensure data integrity before database operations
 */

// Activity creation/update
export const activitySchema = z.object({
  distance_km: z
    .number({ required_error: "La distancia es requerida" })
    .min(0, { message: "La distancia no puede ser negativa" })
    .max(500, { message: "La distancia máxima es 500km" }),
  duration_minutes: z
    .number({ required_error: "La duración es requerida" })
    .int({ message: "La duración debe ser un número entero" })
    .positive({ message: "La duración debe ser mayor a 0" })
    .max(1440, { message: "La duración máxima es 24 horas" }),
  duration_seconds: z
    .number()
    .int()
    .positive({ message: "La duración debe ser mayor a 0" })
    .max(86400, { message: "La duración máxima es 24 horas" })
    .optional()
    .nullable(),
  intensity: z
    .enum(["light", "moderate", "intense"], {
      errorMap: () => ({ message: "Intensidad inválida" }),
    })
    .optional()
    .nullable(),
  photo_url: z.string().url().optional().nullable(),
  description: z.string().max(280, { message: "Máximo 280 caracteres" }).optional().nullable(),
  metadata: z.record(z.any()).optional().nullable(),
});

// Profile updates
export const profileUpdateSchema = z.object({
  full_name: z
    .string()
    .max(100, { message: "El nombre debe tener menos de 100 caracteres" })
    .optional()
    .nullable(),
  runner_level: z
    .enum(["beginner", "intermediate", "pro"], {
      errorMap: () => ({ message: "Nivel de ranerzzz inválido" }),
    })
    .optional()
    .nullable(),
  avatar_url: z.string().url().optional().nullable(),
  max_distance: z
    .enum(["5k", "10k", "21k", "42k"], {
      errorMap: () => ({ message: "Distancia inválida" }),
    })
    .optional()
    .nullable(),
  goals: z
    .array(
      z.enum([
        "weight_loss",
        "first_race",
        "improve_times",
        "meet_people",
        "have_fun",
        "health",
      ])
    )
    .max(10, { message: "Máximo 10 objetivos" })
    .optional(),
  sports: z
    .array(
      z.enum([
        "running",
        "trail",
        "cycling",
        "swimming",
        "padel",
        "tennis",
        "gym",
        "yoga",
        "crossfit",
        "other",
      ])
    )
    .max(15, { message: "Máximo 15 deportes" })
    .optional(),
  how_found_us: z
    .enum(["instagram", "friend", "event", "google", "other"], {
      errorMap: () => ({ message: "Opción inválida" }),
    })
    .optional()
    .nullable(),
  neighborhood: z
    .string()
    .max(100, { message: "El barrio debe tener menos de 100 caracteres" })
    .optional()
    .nullable(),
  onboarding_completed_at: z.string().datetime().optional().nullable(),
  // New onboarding fields
  birth_date: z.string().optional().nullable(),
  phone: z.string().max(20).optional().nullable(),
  emergency_contact_name: z.string().max(100).optional().nullable(),
  emergency_contact_phone: z.string().max(20).optional().nullable(),
  medical_notes: z.string().max(500).optional().nullable(),
  pace_range: z.enum(["slow", "medium", "fast"]).optional().nullable(),
  accepted_responsibility: z.boolean().optional(),
  dni: z.string().max(20).optional().nullable(),
  blood_type: z.string().max(10).optional().nullable(),
  health_provider: z.string().max(100).optional().nullable(),
  accepted_responsibility_at: z.string().datetime().optional().nullable(),
  accepted_terms_version: z.string().max(10).optional().nullable(),
  feed_card_color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, { message: "Color inválido (usar formato #rrggbb)" })
    .optional()
    .nullable(),
  current_focus: z.string().max(50).optional().nullable(),
  objective: z.string().max(50).optional().nullable(),
  has_injury: z.boolean().optional().nullable(),
  injury_notes: z.string().max(500).optional().nullable(),
  strava_connected: z.boolean().optional(),
  strava_athlete_id: z.string().optional().nullable(),
  strava_auto_publish: z.boolean().optional(),
});

// Legacy onboarding schema (kept for compatibility)
export const onboardingSchema = z.object({
  runner_level: z.enum(["beginner", "intermediate", "pro"]).optional(),
  max_distance: z.enum(["5k", "10k", "21k", "42k"]).optional(),
  goals: z.array(z.string()).optional(),
  sports: z.array(z.string()).optional(),
  how_found_us: z.enum(["instagram", "friend", "event", "google", "other"]).optional(),
  neighborhood: z.string().max(100).optional(),
  onboarding_completed_at: z.string().datetime(),
});

// New 3-step onboarding schema
export const newOnboardingSchema = z.object({
  // Step 1 - Required
  full_name: z.string().min(2, "Ingresá tu nombre").max(100),
  birth_date: z.string().min(1, "Ingresá tu fecha de nacimiento"),
  phone: z.string().min(6, "Ingresá tu teléfono").max(20),
  // Step 1 - Recommended
  dni: z.string().max(20).optional().nullable(),
  blood_type: z.string().max(10).optional().nullable(),
  health_provider: z.string().max(100).optional().nullable(),
  // Step 2 - Required
  emergency_contact_name: z.string().min(2, "Ingresá el nombre del contacto").max(100),
  emergency_contact_phone: z.string().min(6, "Ingresá el teléfono del contacto").max(20),
  accepted_responsibility: z.literal(true, {
    errorMap: () => ({ message: "Tenés que aceptar la declaración de responsabilidad" }),
  }),
  // Step 2 - Recommended
  medical_notes: z.string().max(500).optional().nullable(),
  // Step 3 - Recommended
  runner_level: z.enum(["beginner", "intermediate", "pro"]).optional().nullable(),
  current_focus: z.enum(["starting", "comeback", "improve_pace", "prepare_race", "social", "wellness"]).optional().nullable(),
  objective: z.enum(["undecided", "meet_people", "health", "improve_times"]).optional().nullable(),
  has_injury: z.boolean().optional().nullable(),
  // Step 3 — optional injury notes
  injury_notes: z.string().max(500).optional().nullable(),
  // Programmatic
  onboarding_completed_at: z.string().datetime(),
  accepted_responsibility_at: z.string().datetime().optional().nullable(),
  accepted_terms_version: z.string().optional().nullable(),
});

// Auth: login / signup / reset
export const loginSchema = z.object({
  email: z.string().email("Email inválido").max(254),
  password: z.string().min(6, "La contraseña debe tener al menos 6 caracteres").max(72),
});

export const signupSchema = z.object({
  fullName: z
    .string()
    .min(2, "Ingresá tu nombre y apellido")
    .max(100)
    .refine(
      (v) => v.trim().split(/\s+/).filter((w) => w.length >= 2).length >= 2,
      { message: "Ingresá nombre y apellido (ej: Juan Pérez)" },
    ),
  email: z.string().email("Email inválido").max(254),
  password: z.string().min(6, "La contraseña debe tener al menos 6 caracteres").max(72),
});

export const resetPasswordSchema = z.object({
  email: z.string().email("Email inválido").max(254),
});

// Type exports for use in components
export type ActivityInput = z.infer<typeof activitySchema>;
export type ProfileUpdate = z.infer<typeof profileUpdateSchema>;
export type OnboardingData = z.infer<typeof onboardingSchema>;
export type NewOnboardingData = z.infer<typeof newOnboardingSchema>;

/**
 * Validate data with a schema and return result
 * @returns { success: true, data } or { success: false, error }
 */
export function validateData<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; error: string } {
  const result = schema.safeParse(data);
  
  if (result.success) {
    return { success: true, data: result.data };
  }
  
  // Get the first error message
  const firstError = result.error.errors[0];
  return {
    success: false,
    error: firstError?.message || "Datos inválidos",
  };
}
